import { create } from 'zustand';
import odooClient from '../services/odooClient';

const DEFAULT_CONTACT_ORDER = 'create_date desc';

const resolveContactOrder = (order?: string) =>
  typeof order === 'string' && order.trim().length > 0
    ? order
    : DEFAULT_CONTACT_ORDER;

// Helper function to transform Odoo contacts to our format
export const transformOdooContact = (odooContact: any): Contact => ({
  id: odooContact.id.toString(),
  name: odooContact.name || '',
  FirstName: odooContact.FirstName || '',
  LastName: odooContact.LastName || '',
  email: odooContact.email || '',
  phone: odooContact.phone_number_full || odooContact.phone || '',
  city: odooContact.city || '',
  country_id: odooContact.country_id,
  client_classifications: odooContact.client_classifications,
  is_company: odooContact.is_company || false,
  customer_rank: odooContact.customer_rank || 0,
  total_sales_order: odooContact.total_sales_order || 0,
  create_date: odooContact.create_date || '',
  write_date: odooContact.write_date || '',
  interest_date: odooContact.interest_date || '',
  interest_added_date:
    odooContact.interest_added_date ||
    odooContact.date_added ||
    odooContact.relation_create_date ||
    '',
  partner_interest_date: odooContact.partner_interest_date || '',
  is_partner_lead: odooContact.is_partner_lead || false,
  is_partner_client: odooContact.is_partner_client || false,

  // Contact Detail fields
  title: undefined,
  title_ids: (() => {
    try {
      if (Array.isArray(odooContact.title_ids)) {
        return odooContact.title_ids
          .map((t: any) => (Array.isArray(t) ? t[0] : t))
          .filter((id: any) => typeof id === 'number');
      }
    } catch (_) { /* noop */ }
    return [];
  })(),
  gallery_order_count:
    odooContact.number_of_invoices ??
    odooContact.gallery_order_count ??
    odooContact.gallery_order_count_filemaker ??
    0,
  control_galleries_ids: odooContact.control_galleries_ids,

  // Additional emails and phones
  Email1: odooContact.Email1,
  Email2: odooContact.Email2,
  Phone1: odooContact.Phone1,
  Phone2: odooContact.Phone2,
  phone_country_code: odooContact.phone_country_code,
  mobile_country_code: odooContact.mobile_country_code,

  // Customer ID for sales
  Id: odooContact.Id,
  // FileMaker ID (preferred external reference)
  filemaker_id: (odooContact as any).filemaker_id,
  IdEntity: (odooContact as any).Id_with_c || '',

  Id_with_c: (odooContact as any).Id_with_c || false,

  // Address fields
  street: odooContact.street || '',
  street2: odooContact.street2,
  main_street3: (odooContact as any).main_street3,
  state_id: odooContact.state_id,
  zip: odooContact.zip || '',

  // Shipping address
  ShippingAddress: odooContact.ShippingAddress,
  Shipping_stree2: (odooContact as any).Shipping_stree2 || false,
  Shipping_stree3: (odooContact as any).Shipping_stree3 || false,
  ShippingAddress2: (odooContact as any).ShippingAddress2 || false,
  Country_Shipping_Address: odooContact.Country_Shipping_Address || '',
  State_Shipping_Address: odooContact.State_Shipping_Address || '',
  City_Shipping_Address: odooContact.City_Shipping_Address || '',
  Zip_Shipping_Address: odooContact.Zip_Shipping_Address || '',

  // Billing address
  BillingAddress: odooContact.BillingAddress,
  Country_Billing_Address: odooContact.Country_Billing_Address || '',
  State_Billing_Address: odooContact.State_Billing_Address || '',
  City_Billing_Address: odooContact.City_Billing_Address || '',
  Zip_Billing_Address: odooContact.Zip_Billing_Address || '',

  // Personal info
  BirthDate: odooContact.BirthDate,

  // Artist interests
  interest_artist_ids: odooContact.interest_artist_ids,

  // Additional editable fields for new contacts (using existing Odoo fields)
  website: odooContact.website,
  function: odooContact.function, // Job position field in Odoo
  lang: odooContact.lang, // Language field in Odoo  
  mobile: odooContact.mobile, // Additional phone field
  company_type: odooContact.company_type, // 'person' or 'company'
  parent_id: odooContact.parent_id, // Company reference in Odoo
  Company: odooContact.Company, // Company field in Odoo (capital C)
  seller_ids: Array.isArray(odooContact.seller_ids)
    ? odooContact.seller_ids
        .map((entry: any) => {
          if (typeof entry === 'number') return entry;
          if (typeof entry === 'string') {
            const parsed = parseInt(entry, 10);
            return Number.isFinite(parsed) ? parsed : entry;
          }
          if (Array.isArray(entry) && typeof entry[0] === 'number') return entry[0];
          return entry;
        })
        .filter((entry: any) => entry !== null && entry !== undefined)
    : (typeof odooContact.seller_ids === 'number' ? [odooContact.seller_ids] : []),
  seller_fm: odooContact.seller_fm,
  Vip: odooContact.Vip,
  last_purchase: odooContact.last_purchase_invoices || odooContact.last_purchase,
  countries_Country: odooContact.countries_Country
  ,
  // CRM category
  category:
    (odooContact as any).category ||
    (odooContact as any).Category ||
    (typeof (odooContact as any).category_crm === 'string' ? (odooContact as any).category_crm : '') ||
    (Array.isArray((odooContact as any).category_crm_ids) && typeof (odooContact as any).category_crm_ids[1] === 'string'
      ? (odooContact as any).category_crm_ids[1]
      : ''),
  category_crm: odooContact.category_crm,

  category_crm_ids: odooContact.category_crm_ids,
  // Calculated status (Many2one)
  calculated_status_ids: odooContact.calculated_status_ids,

  // Newly requested additional fields
  international_code_phone1: odooContact.international_code_phone1,
  international_code_phone_pro: odooContact.international_code_phone_pro,
  international_code_phone2: odooContact.international_code_phone2,
  birth_date: odooContact.birth_date,
  birth_date_partner: odooContact.birth_date_partner,
  partner_name: odooContact.partner_name,
  email_pro: odooContact.email_pro,
  phone_pro: odooContact.phone_pro,
  social_link: odooContact.social_link,
  show_personal_info: (odooContact as any).show_personal_info !== undefined ? !!(odooContact as any).show_personal_info : undefined,
  _model: odooContact._model || 'res.partner'
});

export interface Contact {
  id: string;
  name: string;
  FirstName?: string | false;
  LastName?: string | false;
  email?: string;
  phone?: string;
  city?: string;
  country_id?: any;
  client_classifications?: any;
  is_company?: boolean;
  customer_rank?: number;
  total_sales_order?: number;
  create_date?: string;
  write_date?: string;
  interest_date?: string;
  interest_added_date?: string;
  partner_interest_date?: string;
  is_partner_lead?: boolean;
  is_partner_client?: boolean;

  // Contact Detail fields
  title?: any; // Can be ID (for creation) or Many2One array (from Odoo)
  title_ids?: number[];
  gallery_order_count?: number;
  control_galleries_ids?: number | [number, string] | false; // Gallery (Many2One to control.galleries)

  // Additional emails and phones
  Email1?: string | false;
  Email2?: string | false;
  Phone1?: string | false;
  Phone2?: string | false;
  phone_country_code?: string | false;
  mobile_country_code?: string | false;

  // Customer ID for sales
  Id?: number | false;
  // FileMaker ID
  filemaker_id?: number | string | false;
  IdEntity?: string | false;
  Id_with_c?: string | false;

  // Address fields
  street?: string;
  street2?: string | false;
  main_street3?: string | false;
  state_id?: [number, string] | false;
  zip?: string;

  // Shipping address
  ShippingAddress?: string | false;
  Shipping_stree2?: string | false;
  Shipping_stree3?: string | false;
  ShippingAddress2?: string | false;

  ShippingAddress3?: string | false;

  Country_Shipping_Address?: string;
  State_Shipping_Address?: string;
  City_Shipping_Address?: string;
  Zip_Shipping_Address?: string;

  // Billing address
  BillingAddress?: string | false;
  Country_Billing_Address?: string;
  State_Billing_Address?: string;
  City_Billing_Address?: string;
  Zip_Billing_Address?: string;

  // Personal info
  BirthDate?: string | false;

  // Artist interests
  interest_artist_ids?: any[];

  // Additional editable fields for new contacts (using existing Odoo fields)
  website?: string | false;
  function?: string | false; // Job position field in Odoo
  lang?: string | false; // Language field in Odoo  
  mobile?: string | false; // Additional phone field
  company_type?: string; // 'person' or 'company'
  parent_id?: number | [number, string] | false; // Company reference in Odoo
  Company?: string; // Company field in Odoo (capital C)
  seller_ids?: number[] | Array<[number, string]> | false; // Sellers (Many2many to res.users)
  seller_fm?: string | null; // FileMaker seller string
  Vip?: string | boolean;
  last_purchase?: string;
  countries_Country?: any;
  category?: string | false;
  // CRM category
  category_crm?: string | false;

  category_crm_ids?: number | [number, string] | false;
  // Newly requested additional fields
  international_code_phone1?: string | false;
  international_code_phone_pro?: string | false;
  international_code_phone2?: string | false;
  birth_date?: string | false;
  birth_date_partner?: string | false;
  partner_name?: string | false;
  email_pro?: string | false;
  phone_pro?: string | false;
  social_link?: string | false;

  show_personal_info?: boolean;
  calculated_status_ids?: number | [number, string] | false;
  _allAddresses?: any[];

  _model?: 'crm.prospect' | 'res.partner';
}

const hydrateContactsWithAddressData = async (contacts: Contact[]): Promise<Contact[]> => {
  try {
    if (!Array.isArray(contacts) || contacts.length === 0) return contacts;

    const partnerIds = contacts
      .map((c) => Number(c.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (partnerIds.length === 0) return contacts;

    const addressMap = await odooClient.getAddressesForMultiplePartners(partnerIds);

    return contacts.map((contact) => {
      const id = Number(contact.id);
      const addresses = addressMap.get(id) || [];
      const primaryAddress =
        addresses.find((a: any) => a?.is_main === true) ||
        addresses.find((a: any) => typeof a?.City === 'string' || typeof a?.Country === 'string') ||
        addresses[0];

      const baseCity = typeof contact.city === 'string' ? contact.city.trim() : '';
      const shippingCity = typeof contact.City_Shipping_Address === 'string' ? contact.City_Shipping_Address.trim() : '';
      const addressCity = typeof primaryAddress?.City === 'string' ? primaryAddress.City.trim() : '';

      const textCountry = typeof contact.countries_Country === 'string' ? contact.countries_Country.trim() : '';
      const addressCountry = typeof primaryAddress?.Country === 'string' ? primaryAddress.Country.trim() : '';

      return {
        ...contact,
        _allAddresses: addresses,
        city: baseCity || shippingCity || addressCity || '',
        City_Shipping_Address: shippingCity || addressCity || '',
        countries_Country: textCountry || addressCountry || contact.countries_Country,
      };
    });
  } catch (error) {
    console.warn('Failed to hydrate contacts with address data:', error);
    return contacts;
  }
};

const normalizeFilterValues = (values?: Array<string | number>): string[] =>
  Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    ),
  );

const applyClientTypeFilters = (
  contacts: Contact[],
  clientTypes?: string[],
): Contact[] => {
  const normalizedTypes = Array.from(
    new Set(
      (clientTypes || [])
        .map((type) => String(type || "").trim().toLowerCase())
        .filter(Boolean),
    ),
  );

  if (normalizedTypes.length === 0) return contacts;

  const getContactTypeCandidates = (contact: Contact): string[] => {
    const values: string[] = [];

    const pushIfString = (value: unknown) => {
      if (typeof value === "string" && value.trim()) {
        values.push(value.trim().toLowerCase());
      }
    };

    pushIfString((contact as any).category);
    pushIfString(contact.category_crm);
    pushIfString((contact as any).category_name);
    if (Array.isArray(contact.category_crm_ids) && typeof contact.category_crm_ids[1] === "string") {
      pushIfString(contact.category_crm_ids[1]);
    }

    return Array.from(new Set(values));
  };

  return contacts.filter((contact) => {
    const candidates = getContactTypeCandidates(contact);
    return normalizedTypes.some((type) => candidates.includes(type));
  });
};

const applyStrictAddressLocationFilters = (
  contacts: Contact[],
  filters?: {
    countries?: Array<string | number>;
    regions?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
  },
): Contact[] => {
  const countries = normalizeFilterValues(filters?.countries);
  const regions = normalizeFilterValues(filters?.regions);
  const states = normalizeFilterValues(filters?.states);
  const cities = normalizeFilterValues(filters?.cities);
  const hasStrictLocationFilters = regions.length > 0;

  if (!hasStrictLocationFilters) return contacts;

  return contacts.filter((contact) => {
    const addressMatches = Array.isArray(contact._allAddresses)
      ? contact._allAddresses.some((addr) => {
        const addressCountry =
          typeof addr?.Country === "string" ? addr.Country.trim().toLowerCase() : "";
        const addressRegion =
          typeof addr?.Region === "string" ? addr.Region.trim().toLowerCase() : "";
        const addressState =
          typeof addr?.State === "string" ? addr.State.trim().toLowerCase() : "";
        const addressCity =
          typeof addr?.City === "string" ? addr.City.trim().toLowerCase() : "";

        const matchesCountry = countries.length === 0 || countries.includes(addressCountry);
        const matchesRegion = regions.length === 0 || regions.includes(addressRegion);
        const matchesState = states.length === 0 || states.includes(addressState);
        const matchesCity = cities.length === 0 || cities.includes(addressCity);

        return matchesCountry && matchesRegion && matchesState && matchesCity;
      })
      : false;

    if (addressMatches) return true;
    if (regions.length > 0) return false;

    const candidateCountries = new Set<string>();
    const candidateStates = new Set<string>();
    const candidateCities = new Set<string>();

    const pushText = (set: Set<string>, value: unknown) => {
      if (typeof value === "string" && value.trim()) {
        set.add(value.trim().toLowerCase());
      }
    };

    if (Array.isArray(contact.country_id) && typeof contact.country_id[1] === "string") {
      pushText(candidateCountries, contact.country_id[1]);
    }
    pushText(candidateCountries, contact.countries_Country);
    pushText(candidateCountries, contact.Country_Shipping_Address);
    pushText(candidateCountries, contact.Country_Billing_Address);

    if (Array.isArray(contact.state_id) && typeof contact.state_id[1] === "string") {
      pushText(candidateStates, contact.state_id[1]);
    }
    pushText(candidateStates, contact.State_Shipping_Address);
    pushText(candidateStates, contact.State_Billing_Address);

    pushText(candidateCities, contact.city);
    pushText(candidateCities, contact.City_Shipping_Address);
    pushText(candidateCities, contact.City_Billing_Address);

    const matchesCountry =
      countries.length === 0 || countries.some((country) => candidateCountries.has(country));
    const matchesState =
      states.length === 0 || states.some((state) => candidateStates.has(state));
    const matchesCity =
      cities.length === 0 || cities.some((city) => candidateCities.has(city));

    return matchesCountry && matchesState && matchesCity;
  });
};

const applyInterestDateFallbackFilter = (
  contacts: Contact[],
  filters?: {
    interestDateFrom?: string;
    interestDateTo?: string;
  },
): Contact[] => {
  const from = (filters?.interestDateFrom || "").trim();
  const to = (filters?.interestDateTo || "").trim();
  if (!from && !to) return contacts;

  const fromDate = from ? new Date(`${from}T00:00:00`) : null;
  const toDate = to ? new Date(`${to}T23:59:59.999`) : null;

  return contacts.filter((contact: any) => {
    const rawDate =
      contact.interest_date ||
      contact.interest_added_date ||
      contact.partner_interest_date ||
      "";
    if (!rawDate) return false;

    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return false;

    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  });
};

interface ContactState {
  contacts: Contact[];
  totalCount: number;
  clientsCount: number;  // New: Track clients count separately
  prospectsCount: number; // New: Track prospects count separately
  currentDataType: 'all' | 'clients' | 'prospects' | null; // New: Track what data is loaded
  currentPage: number; // New: Track current page
  itemsPerPage: number; // New: Track items per page
  isFiltered: boolean; // New: Track if current data is filtered
  loading: boolean;
  backgroundLoading: boolean;
  isFetching: boolean; // The "Do Not Disturb" flag
  requestId: number; // Incremented for each fetch so stale responses can be ignored
  loadContacts: (page?: number, itemsPerPage?: number, force?: boolean, order?: string) => Promise<void>;
  loadClients: (page?: number, itemsPerPage?: number, force?: boolean, order?: string) => Promise<void>;
  loadProspects: (page?: number, itemsPerPage?: number, force?: boolean, order?: string) => Promise<void>;
  setContacts: (contacts: Contact[]) => void;
  startBackgroundLoading: () => Promise<void>;
  preloadCounts: () => Promise<void>; // New: Preload all counts
  searchContacts: (searchTerm: string, type?: 'all' | 'clients' | 'prospects') => Promise<Contact[]>; // New: Search method
  loadContactsWithFilters: (filters: any, page?: number, itemsPerPage?: number) => Promise<void>; // New: Load with filters
  loadAllTabsWithFilters: (
    filters: {
      interestArtists?: Array<string | number>;
      interestDateFrom?: string;
      interestDateTo?: string;
      purchaseArtists?: Array<string | number>;
      clientTypes?: string[];
      countries?: Array<string | number>;
      regions?: Array<string | number>;
      states?: Array<string | number>;
      cities?: Array<string | number>;
      purchaseRanges?: string[];
      minPrice?: string;
      maxPrice?: string;
      lastPurchaseRanges?: string[];
    },
    activeType: 'all' | 'clients' | 'prospects',
    page?: number,
    itemsPerPage?: number,
    order?: string
  ) => Promise<void>;
  getClients: () => Contact[];
  getProspects: () => Contact[];
  setItemsPerPage: (value: number) => void;
}

export const useContactStore = create<ContactState>((set, get) => ({
  contacts: [],
  totalCount: 0,
  clientsCount: 0,     // New: Initialize clients count
  prospectsCount: 0,   // New: Initialize prospects count
  currentDataType: null, // New: Track current data type
  currentPage: 1,      // New: Initialize current page
  itemsPerPage: Number(localStorage.getItem('contacts_items_per_page')) || 20,    // New: Initialize items per page
  isFiltered: false, // New: Initialize filtered flag
  loading: false,
  backgroundLoading: false,
  isFetching: false,
  requestId: 0,
  setItemsPerPage: (value: number) => {
    localStorage.setItem('contacts_items_per_page', value.toString());
    set({ itemsPerPage: value });
  },
  setContacts: (contacts) => set({ contacts }),

  // loadContacts: async (page = 1, itemsPerPage = 20, force = false, order?: string) 
  loadContacts: async (page = 1, itemsPerPage = get().itemsPerPage, force = false, order?: string)=> {


    // Check if we already have this exact page loaded for 'all' contacts
    const state = get();
    const isSamePage = state.currentDataType === 'all' && state.currentPage === page && state.itemsPerPage === itemsPerPage;


    const needsReload = state.isFiltered && !force;
    const effectiveOrder = resolveContactOrder(order);
    const orderChanged = order !== undefined;


    if ((!force && !needsReload && !orderChanged && (isSamePage && state.contacts.length > 0)) || (state.isFetching && !force)) {
      console.log('⏭️ Skipping load - already have this page of all contacts or fetching in progress');
      set({ loading: false });
      return;
    }

    if (needsReload) {
      console.log('🔄 Force reload - switching from filtered to unfiltered data');
    }

    console.log('📊 Starting to load page', page, 'with', itemsPerPage, 'contacts per page...');
    set({ requestId: get().requestId + 1, isFetching: true, loading: true });
    const requestId = get().requestId;

    try {
      // If filtered data is currently active, do not overwrite with default loads
      if (get().isFiltered && get().currentDataType === 'all' && !force) {
        console.log('⏭️ Skipping loadContacts set - filtered data active for all');
        set({ loading: false });
        return;
      }
      // Calculate offset for this page
      const offset = (page - 1) * itemsPerPage;

      // Fetch this page of contacts AND total count simultaneously

      const [response, totalCount] = await Promise.all([
        odooClient.getContacts(itemsPerPage, offset, effectiveOrder),
        page === 1 ? odooClient.getContactsCount() : Promise.resolve(state.totalCount) // Only fetch count on first page
      ]);

      if (get().requestId !== requestId) {
        console.log('⏭️ Skipping loadContacts result - stale request', requestId);
        return;
      }

      console.log(' Raw response from Odoo:', response);
      console.log(' Total contacts count:', totalCount);

      if (response && Array.isArray(response)) {
        // Double-check before setting (race guard)
        if (get().isFiltered && get().currentDataType === 'all' && !force) {
          console.log('⏭️ Skipping loadContacts set after fetch - filtered data active for all');
          set({ loading: false });
          return;
        }
        const transformed = response.map(transformOdooContact);
        const hydrated = await hydrateContactsWithAddressData(transformed);
        if (get().requestId !== requestId) {
          console.log('⏭️ Skipping loadContacts hydrate result - stale request', requestId);
          return;
        }
        console.log('✅ Transformed contacts:', hydrated.length, 'contacts');

        set({
          contacts: hydrated,
          totalCount: totalCount || state.totalCount,
          currentDataType: 'all',
          currentPage: page,
          itemsPerPage: itemsPerPage,
          isFiltered: false, // Mark as unfiltered data
          loading: false
        });
      } else {
        console.log('❌ Invalid response format:', response);
        set({ loading: false });
      }
    } catch (error) {
      console.error('❌ Failed to load contacts:', error);
      set({ loading: false });
    } finally {
      if (get().requestId === requestId) set({ isFetching: false });
    }
  },

  // 👥 NEW: Load Clients with dedicated API
  loadClients: async (page = 1, itemsPerPage = get().itemsPerPage, force = false, order?: string) => {


    // Check if we already have this exact page loaded for 'clients'
    const state = get();
    const isSamePage = state.currentDataType === 'clients' && state.currentPage === page && state.itemsPerPage === itemsPerPage;
    const effectiveOrder = resolveContactOrder(order);
    const orderChanged = order !== undefined; // If order is provided, we need to reload


    if ((!force && !orderChanged && (isSamePage && state.contacts.length > 0)) || (state.isFetching && !force)) {
      console.log('⏭️ Skipping client load - already have this page of clients or fetching in progress');
      set({ loading: false });
      return;
    }

    console.log('👥 Starting to load page', page, 'with', itemsPerPage, 'clients per page...');
    set({ requestId: get().requestId + 1, isFetching: true, loading: true });
    const requestId = get().requestId;

    try {
      // If filtered data is currently active for clients, do not overwrite unless we force reload
      if (get().isFiltered && get().currentDataType === 'clients' && !force) {

        set({ loading: false });
        return;
      }
      // Calculate offset for this page
      const offset = (page - 1) * itemsPerPage;

      // Fetch this page of clients AND clients count simultaneously

      const [response, clientsCount] = await Promise.all([
        odooClient.getClients(itemsPerPage, offset, effectiveOrder),
        page === 1 ? odooClient.getClientsCount() : Promise.resolve(state.clientsCount) // Only fetch count on first page
      ]);

      console.log('👥 Raw clients response from Odoo:', response);
      console.log('📊 Total clients count:', clientsCount);

      if (response && Array.isArray(response)) {
        // Double-check before setting (race guard)
        if (get().isFiltered && get().currentDataType === 'clients' && !force) {
          console.log('⏭️ Skipping loadClients set after fetch - filtered data active for clients');
          set({ loading: false });
          return;
        }
        const transformed = response.map(transformOdooContact);
        const hydrated = await hydrateContactsWithAddressData(transformed);
        if (get().requestId !== requestId) {
          console.log('⏭️ Skipping loadClients hydrate result - stale request', requestId);
          return;
        }
        console.log('✅ Transformed clients:', hydrated.length, 'clients');

        set({
          contacts: hydrated,
          clientsCount: clientsCount || state.clientsCount,
          currentDataType: 'clients',
          currentPage: page,
          itemsPerPage: itemsPerPage,
          isFiltered: false, // Mark as unfiltered data
          loading: false
        });
      } else {
        console.log('❌ Invalid clients response format:', response);
        set({ loading: false });
      }
    } catch (error) {
      console.error('❌ Failed to load clients:', error);
      set({ loading: false });
    } finally {
      if (get().requestId === requestId) set({ isFetching: false });
    }
  },

  // 👥 NEW: Load Prospects with dedicated API
  loadProspects: async (page = 1, itemsPerPage = get().itemsPerPage, force = false, order?: string) => {


    // Check if we already have this exact page loaded for 'prospects'
    const state = get();
    const isSamePage = state.currentDataType === 'prospects' && state.currentPage === page && state.itemsPerPage === itemsPerPage;
    const effectiveOrder = resolveContactOrder(order);
    const orderChanged = order !== undefined;


    if ((!force && !orderChanged && (isSamePage && state.contacts.length > 0)) || (state.isFetching && !force)) {
      console.log('⏭️ Skipping prospect load - already have this page of prospects or fetching in progress');
      set({ loading: false });
      return;
    }


    console.log('🎯 Starting to load page', page, 'with', itemsPerPage, 'prospects per page...');
    set({ requestId: get().requestId + 1, isFetching: true, loading: true });
    const requestId = get().requestId;

    try {
      // If filtered data is currently active for prospects, do not overwrite
      if (get().isFiltered && get().currentDataType === 'prospects' && !force) {
        console.log('⏭️ Skipping loadProspects set - filtered data active for prospects');
        set({ loading: false });
        return;
      }
      // Calculate offset for this page
      const offset = (page - 1) * itemsPerPage;

      // Fetch this page of prospects AND prospects count simultaneously
      console.log(`Calling odooClient.getProspects(${itemsPerPage}, ${offset}, ${effectiveOrder}) and getProspectsCount()...`);
      const [response, prospectsCount] = await Promise.all([
        odooClient.getProspects(itemsPerPage, offset, effectiveOrder),
        page === 1 ? odooClient.getProspectsCount() : Promise.resolve(state.prospectsCount) // Only fetch count on first page
      ]);

      console.log('🎯 Raw prospects response from Odoo:', response);
      console.log('📊 Total prospects count:', prospectsCount);

      if (response && Array.isArray(response)) {
        // Double-check before setting (race guard)
        if (get().isFiltered && get().currentDataType === 'prospects' && !force) {
          console.log('⏭️ Skipping loadProspects set after fetch - filtered data active for prospects');
          set({ loading: false });
          return;
        }
        const transformed = response.map(transformOdooContact);
        const hydrated = await hydrateContactsWithAddressData(transformed);
        if (get().requestId !== requestId) {
          console.log('⏭️ Skipping loadProspects hydrate result - stale request', requestId);
          return;
        }
        console.log('✅ Transformed prospects:', hydrated.length, 'prospects');

        set({
          contacts: hydrated,
          prospectsCount: prospectsCount || state.prospectsCount,
          currentDataType: 'prospects',
          currentPage: page,
          itemsPerPage: itemsPerPage,
          isFiltered: false, // Mark as unfiltered data
          loading: false
        });
      } else {
        console.log('❌ Invalid prospects response format:', response);
        set({ loading: false });
      }
    } catch (error) {
      console.error('❌ Failed to load prospects:', error);
      set({ loading: false });
    } finally {
      if (get().requestId === requestId) set({ isFetching: false });
    }
  },

  startBackgroundLoading: async () => {
    // Prevent multiple background loading processes
    if (get().backgroundLoading) {
      return;
    }
    set({ backgroundLoading: true });
    let currentOffset = get().contacts.length;
    const batchSize = 500;

    try {
      while (true) {
        const batchResponse = await odooClient.getContacts(batchSize, currentOffset);
        if (batchResponse && Array.isArray(batchResponse) && batchResponse.length > 0) {
          const transformed = batchResponse.map(transformOdooContact);
          set(state => ({ contacts: [...state.contacts, ...transformed] }));
          currentOffset += batchResponse.length;

          if (batchResponse.length < batchSize) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        } else {
          break;
        }
      }
    } catch (error) {
      console.error('Failed to load remaining contacts:', error);
    } finally {
      set({ backgroundLoading: false });
    }
  },

  preloadCounts: async () => {
    console.log('🚀 PRELOADCOUNTS CALLED!');
    // Don't use isFetching flag here as it would block main data loading
    const requestId = get().requestId + 1;
    set({ requestId });
    try {
      const [totalCount, clientsCount, prospectsCount] = await Promise.all([
        odooClient.getContactsCount(),
        odooClient.getClientsCount(),
        odooClient.getProspectsCount()
      ]);
      if (get().requestId !== requestId) return;
      set({ totalCount, clientsCount, prospectsCount });
      console.log('✅ Preloaded counts:', { totalCount, clientsCount, prospectsCount });
    } catch (error) {
      console.error('❌ Failed to preload counts:', error);
    }
  },


  searchContacts: async (searchTerm: string, type: 'all' | 'clients' | 'prospects' = 'all') => {

    try {

      const response = await odooClient.searchContacts(searchTerm, 50, type);

      if (response && Array.isArray(response)) {
        const transformed = response.map(transformOdooContact);

        // Additional frontend filtering to ensure results actually match the search term
        const searchTermLower = searchTerm.toLowerCase().trim();
        const searchTermOriginal = searchTerm.trim();
        const frontendFiltered = transformed.filter((contact, index) => {
          const nameMatch = contact.name?.toLowerCase().includes(searchTermLower);
          const firstNameMatch = contact.FirstName?.toString().toLowerCase().includes(searchTermLower);
          const lastNameMatch = contact.LastName?.toString().toLowerCase().includes(searchTermLower);
          const emailMatch = contact.email?.toLowerCase().includes(searchTermLower);
          const phoneMatch = contact.phone?.toLowerCase().includes(searchTermLower);
          const cityMatch =
            contact.city?.toLowerCase().includes(searchTermLower) ||
            contact.City_Shipping_Address?.toLowerCase().includes(searchTermLower);

          const rawContact = response[index];
          const email1Match = (rawContact as any)?.Email1?.toString().toLowerCase().includes(searchTermLower);
          const email2Match = (rawContact as any)?.Email2?.toString().toLowerCase().includes(searchTermLower);
          const idEntityMatch = contact.IdEntity?.toString().toLowerCase().includes(searchTermLower) ||
            contact.IdEntity?.toString() === searchTermOriginal;
          const idWithCMatch = (rawContact as any)?.Id_with_c?.toString().toLowerCase().includes(searchTermLower) ||
            (rawContact as any)?.Id_with_c?.toString() === searchTermOriginal;
          const idMatch = idEntityMatch || idWithCMatch;
          return nameMatch || firstNameMatch || lastNameMatch || emailMatch || email1Match || email2Match || phoneMatch || cityMatch || idMatch;
        });

        // console.log('🔍 Suggestions filtered:', frontendFiltered.length, 'contacts');


        return frontendFiltered;
      } else {
        console.log('❌ Invalid search response format:', response);
        return [];
      }
    } catch (error) {
      console.error('❌ Failed to search contacts:', error);
      return [];
    }
  },

  loadContactsWithFilters: async (filters: {
    interestArtists?: Array<string | number>;
    interestDateFrom?: string;
    interestDateTo?: string;
    purchaseArtists?: Array<string | number>;
    clientTypes?: string[];
    countries?: Array<string | number>;
    regions?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    lastPurchaseRanges?: string[];
    galleries?: string[];
    galleryIds?: Array<string | number>;
    spentAtGalleries?: string[];
    type?: 'all' | 'clients' | 'prospects';
    scope?: 'my_contact' | 'my_gallery' | 'worldwide';
  }, page = 1, itemsPerPage = get().itemsPerPage) => {
    console.log('🔍 LOADCONTACTSWITHFILTERS CALLED - filters:', filters, 'page:', page, 'itemsPerPage:', itemsPerPage);
    const previousContacts = get().contacts;
    const requestId = get().requestId + 1;
    // Mark filtered context immediately, keep current rows so UI can blur instead of flashing empty
    set((state) => ({
      ...state,
      requestId,
      isFetching: true,
      loading: true,
      isFiltered: true,
      currentDataType: (filters.type || 'all') as 'all' | 'clients' | 'prospects',
      currentPage: page,
      itemsPerPage
    }));
    try {
      // Fetch counts breakdown in parallel with the paginated list
      const [listRes, counts] = await Promise.all([
        odooClient.searchPartnersWithFilters(filters, itemsPerPage, (page - 1) * itemsPerPage),
        odooClient.getContactsCountBreakdownWithFilters(filters)
      ]);
      const backendFilteredContacts = listRes.contacts;
      const backendTotalCount = listRes.totalCount;
      let contacts = backendFilteredContacts ? backendFilteredContacts.map(transformOdooContact) : [];
      contacts = await hydrateContactsWithAddressData(contacts);
      if (get().requestId !== requestId) {
        console.log('⏭️ Skipping loadContactsWithFilters hydrate result - stale request', requestId);
        return;
      }
      contacts = applyStrictAddressLocationFilters(contacts, {
        countries: filters.countries,
        regions: filters.regions,
        states: filters.states,
        cities: filters.cities,
      });
      contacts = applyInterestDateFallbackFilter(contacts, {
        interestDateFrom: filters.interestDateFrom,
        interestDateTo: filters.interestDateTo,
      });
      let totalFilteredCount = backendTotalCount;
      if (
        contacts.length === 0 &&
        (
          (Array.isArray(filters.regions) && filters.regions.length > 0) ||
          (Array.isArray(filters.states) && filters.states.length > 0) ||
          (Array.isArray(filters.cities) && filters.cities.length > 0)
        ) &&
        Array.isArray(previousContacts) &&
        previousContacts.length > 0
      ) {
        contacts = applyStrictAddressLocationFilters(previousContacts, {
          countries: filters.countries,
          regions: filters.regions,
          states: filters.states,
          cities: filters.cities,
        });
      }

      const contactsMissingVisibility = contacts.filter((c: any) =>
        typeof c.show_personal_info === 'undefined'
      );
      if (contactsMissingVisibility.length > 0) {
        const contactIds = contactsMissingVisibility.map((c: any) => Number(c.id)).filter((id: number) => !isNaN(id));
        if (contactIds.length > 0) {
          try {
            const visibilityMap = await odooClient.getContactsVisibility(contactIds);
            contacts = contacts.map((c: any) => {
              const id = Number(c.id);
              if (visibilityMap.hasOwnProperty(id)) {
                return { ...c, show_personal_info: visibilityMap[id] };
              }
              return c;
            });
          } catch (error) {
            console.warn('Failed to fetch show_personal_info for contacts:', error);
          }
        }
      }

      console.log('✅ Filtered and paginated contacts:', contacts.length, 'contacts');
      console.log('📊 Total filtered count:', totalFilteredCount);

      if (get().requestId !== requestId) {
        console.log('⏭️ Skipping loadContactsWithFilters set - stale request', requestId);
        return;
      }

      const activeType = (filters.type || 'all') as 'all' | 'clients' | 'prospects';
      const activeTabCount =
        typeof totalFilteredCount === 'number' ? totalFilteredCount : contacts.length;

      set((state) => ({
        contacts: contacts,
        totalCount:
          activeType === 'all'
            ? activeTabCount
            : (typeof counts.total === 'number' ? counts.total : state.totalCount),
        clientsCount:
          activeType === 'clients'
            ? activeTabCount
            : (typeof counts.client === 'number' ? counts.client : state.clientsCount),
        prospectsCount:
          activeType === 'prospects'
            ? activeTabCount
            : (typeof counts.lead === 'number' ? counts.lead : state.prospectsCount),
        currentPage: page,
        itemsPerPage: itemsPerPage,
        isFiltered: true,
        loading: false
      }));

    } catch (error) {
      console.error('❌ Failed to load contacts with filters:', error);
      set({ loading: false });
    } finally {
      if (get().requestId === requestId) set({ isFetching: false });
    }
  },

  // Load all tabs (all/clients/prospects) with the same filters concurrently and update counts immediately
  loadAllTabsWithFilters: async (
    filters: {
      interestArtists?: Array<string | number>;
      interestDateFrom?: string;
      interestDateTo?: string;
      purchaseArtists?: Array<string | number>;
      clientTypes?: string[];
      countries?: Array<string | number>;
      regions?: Array<string | number>;
      states?: Array<string | number>;
      cities?: Array<string | number>;
      purchaseRanges?: string[];
      minPrice?: string;
      maxPrice?: string;
      lastPurchaseRanges?: string[];
      galleries?: string[];
      galleryIds?: Array<string | number>;
      spentAtGalleries?: string[];
      scope?: 'my_contact' | 'my_gallery' | 'worldwide';
      searchTerm?: string;
    },
    activeType: 'all' | 'clients' | 'prospects',
    page = 1,
    itemsPerPage = get().itemsPerPage,
    order?: string
  ) => {
    console.log('LOADALLTABSWITHFILTERS CALLED - activeType:', activeType, 'page:', page, 'order:', order, 'searchTerm:', filters.searchTerm);
    const effectiveOrder = resolveContactOrder(order);
    const previousContacts = get().contacts;
    const requestId = get().requestId + 1;
    // Keep visible data; just mark loading so UI can blur
    set((state) => ({
      ...state,
      requestId,
      isFetching: true,
      loading: true,
      isFiltered: true,
      currentDataType: activeType,
      currentPage: page,
      itemsPerPage
    }));

    try {
      const offset = (page - 1) * itemsPerPage;

      // Fetch list for active tab and counts breakdown in parallel
      const [listRes, counts] = await Promise.all([
        // Request the correct subset from the backend so pagination (offset) applies
        // within the active tab (all/clients/prospects), avoiding repeated pages.
        odooClient.searchPartnersWithFilters({ ...filters, type: activeType }, itemsPerPage, offset, effectiveOrder),
        odooClient.getContactsCountBreakdownWithFilters(filters)
      ]);
      let allContacts = Array.isArray(listRes.contacts) ? listRes.contacts.map(transformOdooContact) : [];
      allContacts = await hydrateContactsWithAddressData(allContacts);
      if (get().requestId !== requestId) {
        console.log('⏭️ Skipping loadAllTabsWithFilters hydrate result - stale request', requestId);
        return;
      }
      allContacts = applyStrictAddressLocationFilters(allContacts, {
        countries: filters.countries,
        regions: filters.regions,
        states: filters.states,
        cities: filters.cities,
      });
      allContacts = applyInterestDateFallbackFilter(allContacts, {
        interestDateFrom: filters.interestDateFrom,
        interestDateTo: filters.interestDateTo,
      });
      if (
        allContacts.length === 0 &&
        (
          (Array.isArray(filters.regions) && filters.regions.length > 0) ||
          (Array.isArray(filters.states) && filters.states.length > 0) ||
          (Array.isArray(filters.cities) && filters.cities.length > 0)
        ) &&
        Array.isArray(previousContacts) &&
        previousContacts.length > 0
      ) {
        allContacts = applyStrictAddressLocationFilters(previousContacts, {
          countries: filters.countries,
          regions: filters.regions,
          states: filters.states,
          cities: filters.cities,
        });
      }

      const contactsMissingVisibility = allContacts.filter((c: any) =>
        typeof c.show_personal_info === 'undefined'
      );
      if (contactsMissingVisibility.length > 0) {
        const contactIds = contactsMissingVisibility.map((c: any) => Number(c.id)).filter((id: number) => !isNaN(id));
        if (contactIds.length > 0) {
          try {
            const visibilityMap = await odooClient.getContactsVisibility(contactIds);
            allContacts = allContacts.map((c: any) => {
              const id = Number(c.id);
              if (visibilityMap.hasOwnProperty(id)) {
                return { ...c, show_personal_info: visibilityMap[id] };
              }
              return c;
            });
          } catch (error) {
            console.warn('Failed to fetch show_personal_info for contacts:', error);
          }
        }
      }

      const clientsOnly = allContacts.filter(c => c.is_partner_client === true);
      const prospectsOnly = allContacts.filter(c => c.is_partner_lead === true);

      let displayedContacts: Contact[] = [];
      if (activeType === 'all') displayedContacts = allContacts;
      if (activeType === 'clients') displayedContacts = clientsOnly;
      if (activeType === 'prospects') displayedContacts = prospectsOnly;
      const paginatedDisplayedContacts = displayedContacts;
      const activeTabCount =
        typeof listRes.totalCount === 'number' && listRes.totalCount > 0
          ? listRes.totalCount
          : displayedContacts.length;
      const safeTotalCount =
        typeof counts.total === 'number' && counts.total > 0
          ? counts.total
          : allContacts.length;
      const safeClientsCount =
        typeof counts.client === 'number' && counts.client > 0
          ? counts.client
          : clientsOnly.length;
      const safeProspectsCount =
        typeof counts.lead === 'number' && counts.lead > 0
          ? counts.lead
          : prospectsOnly.length;

      if (get().requestId !== requestId) {
        console.log('⏭️ Skipping loadAllTabsWithFilters set - stale request', requestId);
        return;
      }

      set({
        contacts: paginatedDisplayedContacts,
        totalCount:
          activeType === 'all'
            ? activeTabCount
            : safeTotalCount,
        clientsCount:
          activeType === 'clients'
            ? activeTabCount
            : safeClientsCount,
        prospectsCount:
          activeType === 'prospects'
            ? activeTabCount
            : safeProspectsCount,
        currentPage: page,
        itemsPerPage,
        isFiltered: true,
        loading: false
      });
    } catch (error) {
      console.error('❌ Failed to load all tabs with filters:', error);
      set({ loading: false });
    } finally {
      if (get().requestId === requestId) set({ isFetching: false });
    }
  },

  getClients: () => {
    return get().contacts.filter(contact => contact.is_partner_client === true);
  },

  getProspects: () => {
    return get().contacts.filter(contact => contact.is_partner_lead === true);
  },
}));


export const exportContactsWithFilters = async (
  filters: {
    interestArtists?: Array<string | number>;
    interestDateFrom?: string;
    interestDateTo?: string;
    purchaseArtists?: Array<string | number>;
    clientTypes?: string[];
    countries?: Array<string | number>;
    regions?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    lastPurchaseRanges?: string[];
    galleries?: string[];
    galleryIds?: Array<string | number>;
    spentAtGalleries?: string[];
    scope?: 'my_contact' | 'my_gallery' | 'worldwide';
    searchTerm?: string;
    seller_ids?: Array<string | number>;
  },
  activeType: 'all' | 'clients' | 'prospects',
  order?: string,
  onProgress?: (loaded: number, total: number, message: string) => void
): Promise<Contact[]> => {
  try {

    const rawContacts = await odooClient.exportContactsFast(
      { ...filters, type: activeType, searchTerm: filters.searchTerm },
      order,
      onProgress
    );


    onProgress?.(rawContacts.length, rawContacts.length, "Processing contacts...");
    const allContacts = applyClientTypeFilters(
      rawContacts.map(transformOdooContact),
      filters.clientTypes,
    );

    return allContacts;
  } catch (err) {
    console.error("❌ Export fetch failed:", err);
    return [];
  }
};

